import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FileText, Save } from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { useSettingsStore } from '@/store/settings.store'
import { warehouseService } from '@/services/warehouseService'
import { customersService } from '@/services/customersService'
import { showSaveSuccess } from '@/lib/swal'
import type { CityLookup, CountryLookup, Employee } from '@/api/types'
import type { WarehouseVm } from '../types'
import { employeesService } from '@/services/employeesService'

const BOOL_OPTIONS = [
  { value: 'true', labelKey: 'common.yes' },
  { value: 'false', labelKey: 'common.no' },
] as const

const WAREHOUSE_TYPE_OPTIONS = [
  { value: 'true', labelKey: 'inventoryWarehouses.distribution' },
  { value: 'false', labelKey: 'inventoryWarehouses.storage' },
] as const

export const InventoryWarehouseEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditMode = id !== undefined && id !== 'new'
  const { user } = useAuthStore()
  const { language = 'en' } = useSettingsStore()
  const branchId = user?.selectedBranch?.branchId

  const [countries, setCountries] = useState<CountryLookup[]>([])
  const [cities, setCities] = useState<CityLookup[]>([])
  const [employees, setEmployees] = useState<Employee[]>([])
  const [employeesLoading, setEmployeesLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [vm, setVm] = useState<WarehouseVm>({
    id: 0,
    code: '',
    name: '',
    location: '',
    isTemperatureControlled: false,
    email: '',
    empId: 0,
    wareHouseManagerName: '',
    contactNumber: '',
    operationalHours: '',
    isWarehouseDistribution: true,
    branchId: branchId || 0,
    city: 0,
    country: 0,
  })

  useEffect(() => {
    setVm((prev) => ({ ...prev, branchId: branchId || 0 }))
  }, [branchId])

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const data = await customersService.getCountries()
        setCountries(data)
      } catch {
        setCountries([])
      }
    }
    void loadCountries()
  }, [])

  useEffect(() => {
    if (!vm.country || vm.country <= 0) {
      setCities([])
      return
    }
    const loadCities = async () => {
      try {
        const data = await customersService.getCities(vm.country)
        setCities(data)
      } catch {
        setCities([])
      }
    }
    void loadCities()
  }, [vm.country])

  useEffect(() => {
    if (!isEditMode) return
    const numericId = Number(id)
    if (!numericId || Number.isNaN(numericId)) return

    const load = async () => {
      try {
        setLoading(true)
        const data = await warehouseService.getWarehouseById(numericId)
        setVm({
          id: data.id,
          code: data.code || '',
          name: data.name || '',
          location: data.location || '',
          isTemperatureControlled: Boolean(data.isTemperatureControlled),
          email: data.email || '',
          empId: Number((data as any)?.empId ?? 0),
          wareHouseManagerName: data.wareHouseManagerName || '',
          contactNumber: data.contactNumber || '',
          operationalHours: data.operationalHours || '',
          isWarehouseDistribution: Boolean(data.isWarehouseDistribution),
          branchId: branchId || data.branchId || 0,
          city: data.city || 0,
          country: data.country || 0,
        })
      } catch (error: any) {
        toast.error(error.response?.data?.message || t('inventoryWarehouses.failedToLoadOne'))
      } finally {
        setLoading(false)
      }
    }
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, id, branchId])

  useEffect(() => {
    if (!branchId) return

    const loadEmployees = async () => {
      try {
        setEmployeesLoading(true)
        const res = await employeesService.getAllEmployees(branchId, 1, 1000)
        setEmployees(Array.isArray(res?.data) ? res.data : (res as any)?.data ?? [])
      } catch {
        setEmployees([])
      } finally {
        setEmployeesLoading(false)
      }
    }

    void loadEmployees()
  }, [branchId])

  const getEmployeeFullName = (e?: Employee) => {
    if (!e) return ''
    const fullName =
      language === 'ar'
        ? [e.empFirstNameAr, e.empSecondNameAr, e.empLastNameAr].filter(Boolean).join(' ').trim()
        : [e.empFirstNameEn, e.empSecondNameEn, e.empLastNameEn].filter(Boolean).join(' ').trim()

    return fullName || (e.empCode ? `Employee ${e.empCode}` : '')
  }

  const employeeOptions: SearchableSelectOption[] = useMemo(() => {
    return employees
      .filter((e) => (e?.id ?? 0) > 0)
      .map((e) => ({
        value: String(e.id),
        label: getEmployeeFullName(e) || `Employee ${e.id}`,
      }))
  }, [employees, language])

  const handleEmployeeChange = (value: string) => {
    const nextEmpId = Number(value || 0)
    const selected = employees.find((e) => Number(e.id) === nextEmpId)

    setVm((prev) => ({
      ...prev,
      empId: nextEmpId,
      wareHouseManagerName: nextEmpId ? getEmployeeFullName(selected) : '',
      email: nextEmpId ? String(selected?.empEmail ?? '') : '',
      contactNumber: nextEmpId ? String(selected?.empMobileNo ?? '') : '',
    }))
  }

  useEffect(() => {
    // In edit mode the warehouse may already have its own email/contact (or none),
    // but the user wants the manager's email/contact to always reflect the selected
    // employee after every warehouse load completes.
    if (loading) return
    if (!employees.length) return
    if (!vm.empId) return

    const selected = employees.find((e) => Number(e.id) === Number(vm.empId))
    if (!selected) return

    const nextName = getEmployeeFullName(selected)
    const nextEmail = String(selected.empEmail ?? '')
    const nextContact = String(selected.empMobileNo ?? '')

    setVm((prev) => ({
      ...prev,
      wareHouseManagerName: nextName || prev.wareHouseManagerName,
      email: nextEmail || prev.email,
      contactNumber: nextContact || prev.contactNumber,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vm.empId, employees, language, loading])

  const countryOptions: SearchableSelectOption[] = useMemo(() => {
    return countries.map((c) => ({
      value: String(c.id),
      label: language === 'ar' ? c.lookupNameAr || c.lookupNameEn : c.lookupNameEn || c.lookupNameAr,
    }))
  }, [countries, language])

  const cityOptions: SearchableSelectOption[] = useMemo(() => {
    return cities.map((c) => ({
      value: String(c.id),
      label: language === 'ar' ? c.lookupNameAr || c.lookupNameEn : c.lookupNameEn || c.lookupNameAr,
      disabled: !vm.country,
    }))
  }, [cities, language, vm.country])

  const handleBack = () => navigate('/inventory/warehouses')

  const buildWarehousePayload = (input: WarehouseVm, selectedBranchId: number, forceCreate = false): WarehouseVm => {
    return {
      id: forceCreate ? 0 : Number(input.id || 0),
      code: forceCreate ? String(input.code || '') : String(input.code || ''),
      name: String(input.name || ''),
      location: String(input.location || ''),
      isTemperatureControlled: Boolean(input.isTemperatureControlled),
      email: String(input.email || ''),
      empId: Number(input.empId || 0),
      wareHouseManagerName: String(input.wareHouseManagerName || ''),
      contactNumber: String(input.contactNumber || ''),
      operationalHours: String(input.operationalHours || ''),
      isWarehouseDistribution: Boolean(input.isWarehouseDistribution),
      branchId: Number(selectedBranchId || 0),
      city: Number(input.city || 0),
      country: Number(input.country || 0),
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!branchId) {
      toast.error(t('common.selectBranchFirst'))
      return
    }
    if (!vm.name.trim()) {
      toast.error(t('inventoryWarehouses.validationName'))
      return
    }
    try {
      setSaving(true)
      const payloadToSend = buildWarehousePayload(vm, branchId, !isEditMode)
      // Show strict JSON payload (same as request body)
      // eslint-disable-next-line no-console
      console.log('Warehouse save payload (JSON):\n' + JSON.stringify(payloadToSend, null, 2))

      if (isEditMode) {
        await warehouseService.updateWarehouse(payloadToSend)
      } else {
        await warehouseService.createWarehouse(payloadToSend)
      }
      // Ensure no residual toasts show on success (use Swal like other forms)
      toast.dismiss()
      await showSaveSuccess(isEditMode)
      navigate('/inventory/warehouses')
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('inventoryWarehouses.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('inventoryWarehouses.title')}</h1>
            <p className="text-muted-foreground text-sm">{t('inventoryWarehouses.formSubtitle')}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader className="border-b pb-4">
            <CardTitle className="text-lg">{t('inventoryWarehouses.sectionManagerDetails')}</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <SearchableSelect
                  label={t('inventoryWarehouses.managerName')}
                  options={employeeOptions}
                  value={vm.empId ? String(vm.empId) : ''}
                  onValueChange={handleEmployeeChange}
                  placeholder={t('common.select')}
                  searchPlaceholder={t('common.search')}
                  disabled={loading || saving || employeesLoading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wh-email">{t('inventoryWarehouses.email')}</Label>
                <Input
                  id="wh-email"
                  type="email"
                  value={vm.email}
                  onChange={(e) => setVm((p) => ({ ...p, email: e.target.value }))}
                  disabled={loading || saving}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wh-contact">{t('inventoryWarehouses.contact')}</Label>
                <Input
                  id="wh-contact"
                  value={vm.contactNumber}
                  onChange={(e) => setVm((p) => ({ ...p, contactNumber: e.target.value }))}
                  disabled={loading || saving}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="border-b pb-4">
            <CardTitle className="text-lg">{t('inventoryWarehouses.sectionDetails')}</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {isEditMode && (
                <div className="space-y-2">
                  <Label htmlFor="wh-code">{t('inventoryWarehouses.code')}</Label>
                  <Input id="wh-code" value={vm.code} readOnly />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="wh-name">
                  {t('inventoryWarehouses.name')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="wh-name"
                  value={vm.name}
                  onChange={(e) => setVm((p) => ({ ...p, name: e.target.value }))}
                  disabled={loading || saving}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wh-loc">{t('inventoryWarehouses.location')}</Label>
                <Input
                  id="wh-loc"
                  value={vm.location}
                  onChange={(e) => setVm((p) => ({ ...p, location: e.target.value }))}
                  disabled={loading || saving}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('inventoryWarehouses.tempControlled')}</Label>
                <Select
                  value={String(vm.isTemperatureControlled)}
                  onValueChange={(v) => setVm((p) => ({ ...p, isTemperatureControlled: v === 'true' }))}
                  disabled={loading || saving}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('inventoryWarehouses.selectTemp')} />
                  </SelectTrigger>
                  <SelectContent>
                    {BOOL_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {t(o.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="wh-hours">{t('inventoryWarehouses.operatingHours')}</Label>
                <Input
                  id="wh-hours"
                  value={vm.operationalHours}
                  onChange={(e) => setVm((p) => ({ ...p, operationalHours: e.target.value }))}
                  placeholder={t('inventoryWarehouses.operatingHoursPlaceholder')}
                  disabled={loading || saving}
                />
              </div>
              <div className="space-y-2">
                <SearchableSelect
                  label={t('inventoryWarehouses.country')}
                  options={countryOptions}
                  value={vm.country ? String(vm.country) : ''}
                  onValueChange={(v) => {
                    const next = Number(v || 0)
                    setVm((p) => ({ ...p, country: next, city: 0 }))
                  }}
                  placeholder={t('inventoryWarehouses.selectCountry')}
                  searchPlaceholder={t('common.search')}
                  disabled={loading || saving}
                />
              </div>
              <div className="space-y-2">
                <SearchableSelect
                  label={t('inventoryWarehouses.city')}
                  options={cityOptions}
                  value={vm.city ? String(vm.city) : ''}
                  onValueChange={(v) => setVm((p) => ({ ...p, city: Number(v || 0) }))}
                  placeholder={t('inventoryWarehouses.selectCity')}
                  searchPlaceholder={t('common.search')}
                  disabled={loading || saving || !vm.country}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('inventoryWarehouses.type')}</Label>
                <Select
                  value={String(vm.isWarehouseDistribution)}
                  onValueChange={(v) => setVm((p) => ({ ...p, isWarehouseDistribution: v === 'true' }))}
                  disabled={loading || saving}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('common.select')} />
                  </SelectTrigger>
                  <SelectContent>
                    {WAREHOUSE_TYPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {t(o.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end border-t pt-4">
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={handleBack} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={saving || loading}>
              <Save className="mr-2 h-4 w-4" />
              {isEditMode ? t('common.update') : t('inventoryWarehouses.save')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
